import React, { useEffect } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	FormControl,
	FormHelperText,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import {
	IProviderPayableLinePayment,
	IProviderPayableLinePaymentCreateDTO,
	ProviderPayableLinePaymentTypeEnum,
} from 'features/salesOrder/types';

export const PAYMENT_TYPE_LABELS: Record<ProviderPayableLinePaymentTypeEnum, string> = {
	[ProviderPayableLinePaymentTypeEnum.CorporateCard]: 'Tarjeta Corporativa',
	[ProviderPayableLinePaymentTypeEnum.C2A]: 'C2A (Tarjeta de tercero)',
	[ProviderPayableLinePaymentTypeEnum.BankTransfer]: 'Transferencia Bancaria',
	[ProviderPayableLinePaymentTypeEnum.Check]: 'Cheque',
	[ProviderPayableLinePaymentTypeEnum.Cash]: 'Efectivo',
	[ProviderPayableLinePaymentTypeEnum.Other]: 'Otro',
};

interface FormValues {
	paymentDate: string;
	amount: string;
	paymentType: ProviderPayableLinePaymentTypeEnum | '';
	notes: string;
	observation: string;
	localCurrency: string;
	exchangeRate: string;
}

interface Props {
	existingPayment?: IProviderPayableLinePayment;
	onSubmit: (data: IProviderPayableLinePaymentCreateDTO) => Promise<void>;
	onCancel: () => void;
	loading: boolean;
	maxAmount?: number;
	currency?: string;
}

export const PaymentForm = ({
	existingPayment,
	onSubmit,
	onCancel,
	loading,
	maxAmount,
	currency,
}: Props): JSX.Element => {
	const {
		register,
		handleSubmit,
		control,
		reset,
		formState: { errors },
	} = useForm<FormValues>({
		defaultValues: {
			paymentDate: existingPayment?.paymentDate ?? new Date().toISOString().split('T')[0],
			amount: existingPayment?.amount !== undefined ? String(existingPayment.amount) : '',
			paymentType: existingPayment?.paymentType ?? '',
			notes: existingPayment?.notes ?? '',
			observation: existingPayment?.observation ?? '',
			localCurrency: existingPayment?.localCurrency ?? 'USD',
			exchangeRate: existingPayment?.exchangeRate !== undefined ? String(existingPayment.exchangeRate) : '1',
		},
	});

	useEffect(() => {
		if (existingPayment) {
			reset({
				paymentDate: existingPayment.paymentDate,
				amount: String(existingPayment.amount),
				paymentType: existingPayment.paymentType,
				notes: existingPayment.notes ?? '',
				observation: existingPayment.observation ?? '',
				localCurrency: existingPayment.localCurrency ?? 'USD',
				exchangeRate: existingPayment.exchangeRate !== undefined ? String(existingPayment.exchangeRate) : '1',
			});
		}
	}, [existingPayment, reset]);

	const handleFormSubmit = async (values: FormValues) => {
		const amount = parseFloat(values.amount);
		if (isNaN(amount) || amount === 0) return;
		if (!values.paymentType) return;

		const exchangeRate = parseFloat(values.exchangeRate);
		await onSubmit({
			paymentDate: values.paymentDate,
			amount,
			paymentType: values.paymentType as ProviderPayableLinePaymentTypeEnum,
			notes: values.notes || undefined,
			observation: values.observation || undefined,
			localCurrency: values.localCurrency || 'USD',
			exchangeRate: isNaN(exchangeRate) ? 1 : exchangeRate,
		});
	};

	return (
		<Box component="form" onSubmit={handleSubmit(handleFormSubmit)} noValidate>
			<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
				{existingPayment ? 'Editar pago' : 'Registrar nuevo pago'}
			</Typography>

			<Grid container spacing={2}>
				<Grid item xs={12} sm={6}>
					<TextField
						label="Fecha de pago"
						type="date"
						fullWidth
						size="small"
						InputLabelProps={{ shrink: true }}
						error={!!errors.paymentDate}
						helperText={errors.paymentDate?.message}
						{...register('paymentDate', { required: 'La fecha es requerida' })}
					/>
				</Grid>

				<Grid item xs={12} sm={6}>
					<TextField
						label="Monto (siempre en USD)"
						type="number"
						fullWidth
						size="small"
						inputProps={{ step: '0.01' }}
						{...(existingPayment !== undefined ? { InputLabelProps: { shrink: true } } : {})}
						onWheel={(e) => (e.target as HTMLInputElement).blur()}
						sx={{
							'& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
								WebkitAppearance: 'none',
								margin: 0,
							},
							'& input[type=number]': { MozAppearance: 'textfield' },
						}}
						error={!!errors.amount}
						helperText={errors.amount?.message ?? 'Negativo para nota de crédito o rechazo'}
						{...register('amount', {
							required: 'El monto es requerido',
							validate: (v) => {
								const parsed = parseFloat(v);
								if (isNaN(parsed) || parsed === 0) return 'El monto no puede ser 0';
								if (parsed > 0 && maxAmount !== undefined && parsed > maxAmount) {
									return `El monto supera el pendiente de esta línea (${currency ?? ''} ${maxAmount.toLocaleString(
										'es-UY',
										{ minimumFractionDigits: 2 },
									)})`;
								}
								return true;
							},
						})}
					/>
				</Grid>

				<Grid item xs={12}>
					<FormControl fullWidth size="small" error={!!errors.paymentType}>
						<InputLabel>Tipo de pago</InputLabel>
						<Controller
							name="paymentType"
							control={control}
							rules={{ required: 'El tipo de pago es requerido' }}
							render={({ field }) => (
								<Select label="Tipo de pago" {...field}>
									<MenuItem value="" disabled>
										Seleccionar...
									</MenuItem>
									{Object.values(ProviderPayableLinePaymentTypeEnum).map((type) => (
										<MenuItem key={type} value={type}>
											{PAYMENT_TYPE_LABELS[type]}
										</MenuItem>
									))}
								</Select>
							)}
						/>
						{errors.paymentType && <FormHelperText>{errors.paymentType.message}</FormHelperText>}
					</FormControl>
				</Grid>

				<Grid item xs={12}>
					<TextField
						label="Nota (motivo / referencia de conciliación)"
						placeholder="Ej: BSP W03M05"
						fullWidth
						size="small"
						{...(existingPayment !== undefined ? { InputLabelProps: { shrink: true } } : {})}
						{...register('notes')}
					/>
				</Grid>

				<Grid item xs={12}>
					<TextField
						label="Observación (origen del pago)"
						placeholder="Ej: Banco ITAU TRF 058789 · Auth 013383 XXXX Celina B."
						fullWidth
						size="small"
						{...(existingPayment !== undefined ? { InputLabelProps: { shrink: true } } : {})}
						{...register('observation')}
					/>
				</Grid>

				{/* Moneda y tipo de cambio — informativo para conciliación bancaria */}
				<Grid item xs={12} sm={6}>
					<TextField
						label="Moneda de pago"
						placeholder="USD"
						fullWidth
						size="small"
						helperText="Moneda real de la cuenta bancaria utilizada"
						{...(existingPayment !== undefined ? { InputLabelProps: { shrink: true } } : {})}
						{...register('localCurrency')}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<TextField
						label="Tipo de cambio"
						type="number"
						fullWidth
						size="small"
						inputProps={{ step: '0.0001', min: '0' }}
						helperText="Tasa aplicada al momento del pago (default: 1)"
						{...(existingPayment !== undefined ? { InputLabelProps: { shrink: true } } : {})}
						onWheel={(e) => (e.target as HTMLInputElement).blur()}
						sx={{
							'& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
								WebkitAppearance: 'none',
								margin: 0,
							},
							'& input[type=number]': { MozAppearance: 'textfield' },
						}}
						{...register('exchangeRate')}
					/>
				</Grid>
			</Grid>

			<Box display="flex" justifyContent="flex-end" gap={1} mt={3}>
				<Button variant="outlined" onClick={onCancel} disabled={loading}>
					Cancelar
				</Button>
				<Button
					type="submit"
					variant="contained"
					disabled={loading}
					startIcon={loading ? <CircularProgress size={16} /> : undefined}
				>
					{existingPayment ? 'Guardar cambios' : 'Registrar pago'}
				</Button>
			</Box>
		</Box>
	);
};
